import React, { forwardRef, useImperativeHandle, useRef } from "react"
import { LexicalText } from "@/schema"

import type { EditorRefHandle } from "@/components/lexical"
import type { PlaceholderData } from "@/components/lexical/placeholders"
import { MentionRecord } from "@/components/lexical/plugins/mention-dropdown"

import { MentionUpdateData, useTaskMentions } from "./hooks/use-task-mentions"
import TaskDescriptionEditor from "./task-description-editor"
import TaskTitleEditor from "./task-title-editor"

export type TaskLexicalFieldType = "title" | "description"

type EditorVisibility = {
  title?: boolean
  description?: boolean
}

interface TaskEditorProps {
  onChange?: (lexicalText: LexicalText, type: TaskLexicalFieldType) => void
  placeholder?: string
  descriptionPlaceholder?: string
  initialTitleContent?: LexicalText
  initialDescriptionContent?: LexicalText
  onMentionsUpdate?: (updates: MentionUpdateData) => void
  styleOverrides?: React.CSSProperties | Record<`--${string}`, string>
  show?: EditorVisibility
  onEnterPressNoMention?: () => void
  includeUserMentions?: boolean
  hasExistingRelationship?: boolean
  enablePlaceholders?: boolean
  placeholders?: PlaceholderData[]
  editable?: boolean
}

const TaskEditor = forwardRef<EditorRefHandle, TaskEditorProps>(
  (
    {
      onChange,
      placeholder = "Task title",
      descriptionPlaceholder = "Add description...",
      initialTitleContent,
      initialDescriptionContent,
      onMentionsUpdate,
      styleOverrides,
      show = { title: true, description: true },
      onEnterPressNoMention,
      includeUserMentions = true,
      hasExistingRelationship = false,
      enablePlaceholders,
      placeholders,
      editable = true,
    },
    ref
  ) => {
    const { handleMentionAdded, handleMentionRemoved } = useTaskMentions({
      onMentionUpdate: onMentionsUpdate,
      hasExistingRelationship,
    })

    const handleDescriptionChange = (lexicalText: LexicalText) => {
      onChange?.(lexicalText, "description")
    }

    const handleTitleChange = (lexicalText: LexicalText) => {
      onChange?.(lexicalText, "title")
    }

    const handleTitleMentionAdded = (mention: MentionRecord) => {
      handleMentionAdded(mention, "title")
    }

    const handleDescriptionMentionAdded = (mention: MentionRecord) => {
      handleMentionAdded(mention, "description")
    }

    const titleEditorRef = useRef<EditorRefHandle | null>(null)
    const descriptionEditorRef = useRef<EditorRefHandle | null>(null)
    const lastFocusedRef = useRef<"title" | "description">(show.title ? "title" : "description")

    useImperativeHandle(ref, () => ({
      focus: () => {
        titleEditorRef.current?.focus()
      },
      blur: () => {
        titleEditorRef.current?.blur()
      },
      updateContent: (content: LexicalText) => {
        descriptionEditorRef.current?.updateContent(content)
      },
      getEditor: () => {
        const target = lastFocusedRef.current === "title" ? titleEditorRef : descriptionEditorRef
        return target.current?.getEditor() || null
      },
      getDescriptionEditor: () => descriptionEditorRef.current?.getEditor() || null,
    }))

    // Called when pressing Enter in the title (and mention menu not open)
    // If the parent provided an onEnterPressNoMention callback, use that
    // Otherwise, focus our internal description editor ref
    const focusDescriptionEditor = () => {
      if (onEnterPressNoMention) {
        onEnterPressNoMention()
      } else {
        descriptionEditorRef.current?.focus()
      }
    }

    return (
      <>
        {show.title && (
          <div onFocus={() => (lastFocusedRef.current = "title")}>
            <TaskTitleEditor
              ref={titleEditorRef}
              onChange={handleTitleChange}
              initialContent={initialTitleContent}
              placeholder={placeholder}
              onMentionAdded={handleTitleMentionAdded}
              onMentionRemoved={handleMentionRemoved}
              styleOverrides={styleOverrides}
              onEnterPressNoMention={focusDescriptionEditor}
              includeUserMentions={includeUserMentions}
              enablePlaceholders={enablePlaceholders}
              placeholders={placeholders}
              editable={editable}
            />
          </div>
        )}
        {show.description && (
          <div onFocus={() => (lastFocusedRef.current = "description")}>
            <TaskDescriptionEditor
              ref={descriptionEditorRef}
              onChange={handleDescriptionChange}
              initialContent={initialDescriptionContent}
              placeholder={descriptionPlaceholder}
              onMentionAdded={handleDescriptionMentionAdded}
              onMentionRemoved={handleMentionRemoved}
              styleOverrides={styleOverrides}
              includeUserMentions={includeUserMentions}
              enablePlaceholders={enablePlaceholders}
              placeholders={placeholders}
              editable={editable}
            />
          </div>
        )}
      </>
    )
  }
)

TaskEditor.displayName = "TaskEditor"

export default TaskEditor
